import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import AdminMonitoramento from './paineladmin/AdminMonitoramento';
import LiveMonitor from './LiveMonitor';
import { Database, RotateCcw, Activity, AlertTriangle, Info, AlertCircle, Loader, CheckCircle } from 'lucide-react';

const LOJAS_DISPONIVEIS = ['ANV', 'BVV', 'FER', 'GTV', 'PCI', 'PCS', 'MAL', 'MOX', 'RDP', 'SED', 'SVV', 'VEL', 'VSH', 'LIN'];

const ETL_PROCESSOS = [
    { id: 'ETL_FLEX_SALES',      label: 'ETL Flex Sales',      desc: 'Vendas diárias via API Arcos Dorados',   endpoint: '/api/monitoramento/reprocessar',     minPorDia: 6 },
    { id: 'ETL_SALE_BY_PAYMENT', label: 'ETL Sale by Payment', desc: 'Vendas por tipo de pagamento (InVendas)', endpoint: '/api/monitoramento/reprocessar-sbp', minPorDia: 1 },
];

// ---------------------------------------------------------------------------
// ABA DE REPROCESSAMENTO
// ---------------------------------------------------------------------------
const Reprocessamento = ({ logs }) => {
    const [processo,  setProcesso]  = useState('ETL_FLEX_SALES');
    const [lojasSel,  setLojasSel]  = useState([...LOJAS_DISPONIVEIS]);
    const [inicio,    setInicio]    = useState('');
    const [fim,       setFim]       = useState('');
    const [enviando,  setEnviando]  = useState(false);
    const [resultado, setResultado] = useState(null);   // { ok, log_id, message }

    const token   = localStorage.getItem('authToken');
    const headers = { 'Authorization': `Bearer ${token}` };

    // Derivado dos logs que ETLPanel já atualiza a cada 5s.
    // Filtra SOMENTE pelo processo selecionado — ETLs diferentes podem rodar em paralelo.
    const jobRodando = (logs || []).find(l => l.status === 'rodando' && l.job_name === processo) || null;

    // Cálculo de dias entre datas
    const diasPeriodo = (inicio && fim)
        ? Math.round((new Date(fim) - new Date(inicio)) / (1000 * 60 * 60 * 24)) + 1
        : 0;

    const etlAtual = ETL_PROCESSOS.find(p => p.id === processo);

    // Calcula média de segundos/dia a partir dos runs reais bem-sucedidos deste job.
    // Normaliza pela proporção de lojas selecionadas vs total.
    // Retorna { segsPerDia, totalRuns } ou null se não houver histórico.
    const calcularBaseHistorica = () => {
        const runs = (logs || []).filter(l =>
            l.job_name === processo &&
            ['sucesso', 'parcial'].includes(l.status) &&
            l.duracao_segundos > 0 &&
            l.periodo_inicio &&
            l.periodo_fim
        );
        if (runs.length === 0) return null;

        const valores = runs.map(l => {
            const ini  = new Date(l.periodo_inicio);
            const fim  = new Date(l.periodo_fim);
            const dias = Math.max(Math.round((fim - ini) / 86400000) + 1, 1);
            return l.duracao_segundos / dias;
        });
        const media = valores.reduce((a, b) => a + b, 0) / valores.length;
        return { segsPerDia: media, totalRuns: runs.length };
    };

    const baseHistorica = calcularBaseHistorica();

    const estimarDuracao = (dias) => {
        if (!dias || dias <= 0) return null;
        const ratio   = lojasSel.length / LOJAS_DISPONIVEIS.length;
        const totalSegs = baseHistorica
            ? Math.ceil(baseHistorica.segsPerDia * dias * ratio)
            : Math.ceil(dias * ratio * etlAtual.minPorDia * 60);
        const minutos = Math.ceil(totalSegs / 60);
        if (minutos >= 60) {
            const horas = Math.floor(minutos / 60);
            const min   = minutos % 60;
            return min > 0 ? `~${horas}h ${min}m` : `~${horas}h`;
        }
        return `~${minutos} min`;
    };

    // Monta lista de warnings em tempo real
    const warnings = [];
    if (jobRodando) {
        warnings.push({ type: 'error', icon: AlertCircle, msg: `Já existe um job em execução (#${jobRodando.id}). Aguarde terminar antes de iniciar outro.` });
    }
    if (diasPeriodo > 90) {
        warnings.push({ type: 'danger', icon: AlertTriangle, msg: `Período de ${diasPeriodo} dias é muito grande. Recomendamos dividir em blocos mensais para evitar timeout. Estimativa: ${estimarDuracao(diasPeriodo)}.` });
    } else if (diasPeriodo > 30) {
        warnings.push({ type: 'warning', icon: AlertTriangle, msg: `Período longo: ${diasPeriodo} dias com ${lojasSel.length} loja(s). Estimativa: ${estimarDuracao(diasPeriodo)}.` });
    }
    if (fim && inicio && new Date(fim) < new Date(inicio)) {
        warnings.push({ type: 'error', icon: AlertCircle, msg: 'Data fim não pode ser anterior à data início.' });
    }
    warnings.push({ type: 'info', icon: Info, msg: 'INSERT IGNORE ativo: dados já existentes no banco não serão duplicados. É seguro reprocessar qualquer período.' });

    const podeSalvar = !jobRodando && inicio && fim && new Date(fim) >= new Date(inicio) && lojasSel.length > 0 && !enviando;

    const toggleLoja = (loja) => setLojasSel(prev =>
        prev.includes(loja) ? prev.filter(l => l !== loja) : [...prev, loja]
    );

    const handleSubmit = async () => {
        setEnviando(true);
        setResultado(null);
        try {
            const etl = ETL_PROCESSOS.find(p => p.id === processo);
            const res = await fetch(etl.endpoint, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ lojas: lojasSel.join(','), inicio, fim }),
            });
            const data = await res.json();
            setResultado({ ok: res.ok, ...data });
            if (res.ok && data.log_id) {
                // Polling para atualizar o resultado quando o job terminar
                const poll = setInterval(async () => {
                    const r = await fetch(`/api/monitoramento/logs/${data.log_id}`, { headers });
                    if (r.ok) {
                        const log = await r.json();
                        if (log.data_fim) {
                            clearInterval(poll);
                            setResultado(prev => ({ ...prev, finalizado: true, status: log.status, registros: log.registros_inseridos }));
                        }
                    }
                }, 3000);
            }
        } catch (e) {
            setResultado({ ok: false, message: 'Erro de conexão.' });
        } finally {
            setEnviando(false);
        }
    };

    const warnColors = {
        error:   { bg: '#fff1f2', border: '#fecdd3', color: '#e11d48' },
        danger:  { bg: '#fff7ed', border: '#fed7aa', color: '#c2410c' },
        warning: { bg: '#fffbeb', border: '#fde68a', color: '#d97706' },
        info:    { bg: '#eff6ff', border: '#bfdbfe', color: '#3b82f6' },
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div>

            {/* RESULTADO */}
            {resultado && (
                <div style={{ padding: '16px 20px', borderRadius: '12px', marginBottom: '24px', backgroundColor: resultado.ok ? (resultado.finalizado ? '#ecfdf5' : '#eff6ff') : '#fff1f2', border: `1px solid ${resultado.ok ? (resultado.finalizado ? '#6ee7b7' : '#bfdbfe') : '#fecdd3'}` }}>
                    {resultado.finalizado ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#059669', fontWeight: '700' }}>
                            <CheckCircle size={18} />
                            Reprocessamento concluído! Status: <strong>{resultado.status}</strong> — {resultado.registros?.toLocaleString('pt-BR')} registros inseridos.
                        </div>
                    ) : resultado.ok ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#3b82f6', fontWeight: '700' }}>
                            <Loader size={16} className="animate-spin" /> Log #{resultado.log_id} criado — Reprocessamento em execução. Acompanhe na aba Monitoramento.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e11d48', fontWeight: '700' }}>
                            <AlertCircle size={16} /> {resultado.message}
                        </div>
                    )}
                </div>
            )}

            {/* FORMULÁRIO */}
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '28px', marginBottom: '20px' }}>

                {/* Processo */}
                <div style={{ marginBottom: '24px' }}>
                    <label style={labelStyle}>Processo ETL</label>
                    <select value={processo} onChange={e => setProcesso(e.target.value)} style={inputStyle}>
                        {ETL_PROCESSOS.map(p => (
                            <option key={p.id} value={p.id}>{p.label} — {p.desc}</option>
                        ))}
                    </select>
                </div>

                {/* Período */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    <div>
                        <label style={labelStyle}>Data Início</label>
                        <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Data Fim</label>
                        <input type="date" value={fim} onChange={e => setFim(e.target.value)} style={inputStyle} />
                        {diasPeriodo > 0 && (
                            <p style={{ fontSize: '12px', color: diasPeriodo > 30 ? '#d97706' : '#64748b', margin: '6px 0 0 0', fontWeight: '600' }}>
                                {diasPeriodo} dia{diasPeriodo > 1 ? 's' : ''} · Estimativa: {estimarDuracao(diasPeriodo)}
                                {lojasSel.length < LOJAS_DISPONIVEIS.length && ` · ${lojasSel.length} loja${lojasSel.length > 1 ? 's' : ''}`}
                                {baseHistorica
                                    ? <span style={{ fontWeight: '400', color: '#94a3b8' }}> ({baseHistorica.totalRuns} exec. reais)</span>
                                    : <span style={{ fontWeight: '400', color: '#94a3b8' }}> (estimativa padrão)</span>
                                }
                            </p>
                        )}
                    </div>
                </div>

                {/* Lojas */}
                <div style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <label style={labelStyle}>Lojas ({lojasSel.length}/{LOJAS_DISPONIVEIS.length})</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => setLojasSel([...LOJAS_DISPONIVEIS])} style={smallBtnStyle('#eff6ff', '#3b82f6')}>Todas</button>
                            <button onClick={() => setLojasSel([])} style={smallBtnStyle('#fff1f2', '#e11d48')}>Limpar</button>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {LOJAS_DISPONIVEIS.map(loja => {
                            const ativa = lojasSel.includes(loja);
                            return (
                                <button
                                    key={loja}
                                    onClick={() => toggleLoja(loja)}
                                    style={{ padding: '6px 14px', borderRadius: '20px', border: `2px solid ${ativa ? '#3b82f6' : '#e2e8f0'}`, backgroundColor: ativa ? '#eff6ff' : '#fff', color: ativa ? '#3b82f6' : '#94a3b8', fontWeight: '700', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s' }}
                                >
                                    {loja}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* WARNINGS */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                {warnings.map((w, i) => {
                    const cfg = warnColors[w.type];
                    return (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 16px', borderRadius: '10px', backgroundColor: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color }}>
                            <w.icon size={16} style={{ marginTop: '1px', flexShrink: 0 }} />
                            <span style={{ fontSize: '13px', fontWeight: '600', lineHeight: '1.5' }}>{w.msg}</span>
                        </div>
                    );
                })}
            </div>

            {/* BOTÃO */}
            <button
                onClick={handleSubmit}
                disabled={!podeSalvar}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 28px', borderRadius: '10px', border: 'none', backgroundColor: podeSalvar ? '#3b82f6' : '#e2e8f0', color: podeSalvar ? '#fff' : '#94a3b8', fontWeight: '800', fontSize: '15px', cursor: podeSalvar ? 'pointer' : 'not-allowed', transition: 'all 0.2s' }}
            >
                {enviando ? <Loader size={18} className="animate-spin" /> : <RotateCcw size={18} />}
                {enviando ? 'Iniciando...' : 'Iniciar Reprocessamento'}
            </button>
        </div>

        {/* MONITOR AO VIVO — abaixo do formulário, largura total */}
        <div>
            <LiveMonitor logs={logs} />
        </div>

        </div>
    );
};

const labelStyle = {
    display: 'block', fontSize: '12px', fontWeight: '800',
    color: '#64748b', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: '8px',
};
const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '8px',
    border: '1px solid #e2e8f0', fontSize: '14px', color: '#334155',
    outline: 'none', backgroundColor: '#fff', boxSizing: 'border-box',
};
const smallBtnStyle = (bg, color) => ({
    padding: '4px 10px', borderRadius: '6px', border: `1px solid ${color}20`,
    backgroundColor: bg, color, fontWeight: '700', fontSize: '12px', cursor: 'pointer',
});

// ---------------------------------------------------------------------------
// PÁGINA PRINCIPAL
// ---------------------------------------------------------------------------
const ETLPanel = () => {
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [isMobile,    setIsMobile]    = useState(window.innerWidth <= 768);
    const [activeTab,   setActiveTab]   = useState('monitoramento');
    const [logs,        setLogs]        = useState([]);

    // Atualização "viva" sem piscar a tela (2026-07-07): em vez do refresh cego a
    // cada 5s, só re-renderiza quando os dados MUDAM (compara o payload) e o ritmo
    // se ADAPTA — rápido (5s) só quando há job rodando, calmo (30s) quando tudo parado.
    useEffect(() => {
        const token = localStorage.getItem('authToken');
        let timer = null;
        let ativo = true;
        let ultimo = null;   // último payload aplicado (string) — base da comparação

        const ciclo = async () => {
            let temJobRodando = false;
            try {
                const res = await fetch('/api/monitoramento/logs', { headers: { 'Authorization': `Bearer ${token}` } });
                if (res.ok) {
                    const dados = await res.json();
                    const serial = JSON.stringify(dados);
                    if (serial !== ultimo) {        // só atualiza o estado se MUDOU (sem flicker)
                        ultimo = serial;
                        setLogs(dados);
                    }
                    temJobRodando = Array.isArray(dados) && dados.some(l => l.status === 'rodando');
                }
            } catch (e) { /* silencioso */ }
            if (ativo) timer = setTimeout(ciclo, temJobRodando ? 5000 : 30000);
        };
        ciclo();
        return () => { ativo = false; if (timer) clearTimeout(timer); };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getMarginLeft = () => window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px');

    const tabs = [
        { id: 'monitoramento',   label: 'Monitoramento',   icon: Activity    },
        { id: 'reprocessamento', label: 'Reprocessamento', icon: RotateCcw   },
    ];

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

                {/* HEADER */}
                <div style={{ height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Controladoria de Dados
                        </span>
                        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>Gestão de ETL</h2>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ padding: '10px 14px', backgroundColor: '#eff6ff', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px', color: '#3b82f6', fontWeight: '700', fontSize: '13px' }}>
                            <Database size={16} /> ETL & Integrações
                        </div>
                    </div>
                </div>

                {/* CONTEÚDO */}
                <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

                    {/* NAVEGAÇÃO */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '32px', backgroundColor: '#fff', padding: '8px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        {tabs.map(tab => {
                            const ativo = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', border: 'none', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', backgroundColor: ativo ? '#3b82f6' : 'transparent', color: ativo ? '#fff' : '#64748b', boxShadow: ativo ? '0 4px 6px rgba(59,130,246,0.3)' : 'none' }}
                                >
                                    <tab.icon size={16} /> {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* CONTEÚDO DA ABA */}
                    <div style={{ display: activeTab === 'monitoramento'   ? 'block' : 'none' }}><AdminMonitoramento /></div>
                    <div style={{ display: activeTab === 'reprocessamento' ? 'block' : 'none' }}><Reprocessamento logs={logs} /></div>
                </div>
            </div>
        </div>
    );
};

export default ETLPanel;
